import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { useWorkspace } from "components/Workspace/WorkspaceContext";
import { Routes } from "constants/routes";

export const useFullScreenBreadcrumbs = () => {
  const history = useHistory();
  const { deal_id: dealIdParam, folder_id: folderIdParam } = useParams<{
    deal_id?: string;
    folder_id?: string;
  }>();
  const dealId = dealIdParam != null ? Number(dealIdParam) : null;
  const folderId = folderIdParam != null ? Number(folderIdParam) : null;

  const { deals, foldersByDeal, globalFolders, getOrLoadDealFolders } = useWorkspace();

  // Capture return location on mount (before any replace could overwrite it)
  const returnLocationRef = useRef(history.location.pathname + history.location.search);

  useEffect(() => {
    if (dealId != null) {
      getOrLoadDealFolders(dealId);
    }
  }, [dealId, getOrLoadDealFolders]);

  const dealName = dealId != null ? deals.find((d) => d.id === dealId)?.title : undefined;
  const folders = dealId != null ? (foldersByDeal.get(dealId) ?? []) : globalFolders;
  const folderName = folderId != null ? folders.find((f) => f.id === folderId)?.title : undefined;

  const breadcrumbLinks = useMemo(() => {
    const links: { path: string; title: string }[] = [];

    if (dealId != null) {
      links.push({ title: "Global Workspace", path: Routes.workspaceDeals });
      if (dealName) {
        links.push({
          title: dealName,
          path: Routes.workspaceDeal.replace(":deal_id", String(dealId))
        });
      }
      if (folderId != null && folderName) {
        links.push({
          title: folderName,
          path: Routes.workspaceDealFolder.replace(":deal_id", String(dealId)).replace(":folder_id", String(folderId))
        });
      }
    } else {
      links.push({ title: "Global Workspace", path: Routes.workspaceFolders });
      if (folderId != null && folderName) {
        links.push({
          title: folderName,
          path: Routes.workspaceFolder.replace(":folder_id", String(folderId))
        });
      }
    }

    return links;
  }, [dealId, folderId, dealName, folderName]);

  const goBack = useCallback(() => {
    // Use the last breadcrumb as the return destination (the folder the user was in),
    // falling back to the location captured at mount time.
    // Replace instead of goBack/push so the fullscreen route is removed from history.
    const lastLink = breadcrumbLinks[breadcrumbLinks.length - 1];
    history.replace(lastLink?.path ?? returnLocationRef.current);
  }, [history, breadcrumbLinks]);

  return { breadcrumbLinks, goBack };
};
